/**
 * Media-view events for visual-editor insertions.
 *
 * When a consultant inserts an image in the visual editor and opts in to
 * "track when visitors see this image", the insertion is flagged in the
 * experiment's `elements_to_insert` (trackMedia + a stable insertId).
 *
 * The experiment id is not known while authoring, so the matching `media_view`
 * custom events are materialized here — AFTER the experiment is created — so each
 * one can be linked via `experiment_id`.
 *
 * The durable link between an inserted image and its event is the insertion's
 * `insertId`: the runtime (`applyInsertions` in tnx-core.js / `tnxApplyInsert`
 * in the bundle) stamps it onto the injected DOM node as `data-tnx-insert-id`,
 * so a `[data-tnx-insert-id="…"]` selector targets exactly that element —
 * immune to the image filename, CDN re-encodes, or a later src swap.
 */

import { customEventsService, type CreateCustomEventRequest } from '@/api/customEvents'

/** An `elements_to_insert` entry as produced by the visual editor. */
interface InsertEntry {
  insertId?: string
  trackMedia?: boolean
  mediaEventName?: string
  /** Shared group id — insertions sharing it collapse into one pooled event. */
  mediaGroup?: string
  html?: string
  wrapperType?: 'inline' | 'block' | 'none'
}

/**
 * Build the `media_view` selector for an inserted image.
 *
 * The runtime stamps `data-tnx-insert-id` on the inserted *root* node: the
 * HTML's first element when `wrapperType` is 'none', otherwise the span/div
 * wrapper. So when the root itself is the `<img>` we target it directly;
 * otherwise we target the `<img>` descendant.
 */
export function buildInsertedImageSelector(
  insertId: string,
  html: string,
  wrapperType: 'inline' | 'block' | 'none' = 'inline',
): string {
  let rootIsImg = false
  try {
    const tpl = document.createElement('template')
    tpl.innerHTML = (html || '').trim()
    rootIsImg = tpl.content.firstElementChild?.tagName === 'IMG'
  } catch {
    // Unparseable HTML — fall back to the descendant form.
  }
  return wrapperType === 'none' && rootIsImg
    ? `img[data-tnx-insert-id="${insertId}"]`
    : `[data-tnx-insert-id="${insertId}"] img`
}

/**
 * Convert a display name to a snake_case event_name slug.
 * Result matches `^[a-z][a-z0-9_]*$`; capped at 40 chars to leave room for the
 * experiment suffix added by the caller.
 */
export function toEventNameSlug(displayName: string): string {
  let name = (displayName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!name) name = 'inserted_image_seen'
  if (!/^[a-z]/.test(name)) name = `img_${name}`
  return name.slice(0, 40).replace(/_+$/, '')
}

export interface MediaViewMaterializeResult {
  created: number
  failed: number
}

/**
 * Create a `media_view` custom event for every visual-editor insertion flagged
 * with `trackMedia`. Call this once, right after the experiment is created.
 *
 * Non-fatal: a failure on one event is logged and skipped so it never blocks
 * experiment creation.
 *
 * @param variantConfig the treatment variant config (the unified VE config)
 * @param experimentId  the newly created experiment's id
 * @param storeId       the current store id
 */
export async function createMediaViewEventsForInsertions(
  variantConfig: { elements_to_insert?: unknown } | null | undefined,
  experimentId: string,
  storeId: string,
): Promise<MediaViewMaterializeResult> {
  const result: MediaViewMaterializeResult = { created: 0, failed: 0 }

  const inserts = Array.isArray(variantConfig?.elements_to_insert)
    ? (variantConfig!.elements_to_insert as InsertEntry[])
    : []
  const flagged = inserts.filter(i => i && i.trackMedia && i.insertId)
  if (flagged.length === 0) return result

  // event_id is keyed by name + store; suffix with an experiment token so the
  // same event name reused across experiments doesn't collide.
  const expToken = ((experimentId.match(/[a-z0-9]+/gi) || []).join('') || 'exp')
    .toLowerCase()
    .slice(-8)

  // One event per group (insertions sharing a mediaGroup — multi-product
  // experiments pool every product's image into a single event), plus one
  // event per ungrouped insertion (single gallery add / InsertEditor).
  const specs: Array<{ displayName: string; selector: string; tag: string }> = []
  const groups = new Map<string, InsertEntry[]>()
  for (const ins of flagged) {
    if (ins.mediaGroup) {
      const arr = groups.get(ins.mediaGroup)
      if (arr) arr.push(ins)
      else groups.set(ins.mediaGroup, [ins])
    } else {
      specs.push({
        displayName: (ins.mediaEventName || '').trim() || 'Inserted image seen',
        selector: buildInsertedImageSelector(ins.insertId as string, ins.html || '', ins.wrapperType || 'inline'),
        tag: `insert:${ins.insertId}`,
      })
    }
  }
  for (const [groupId, members] of groups) {
    const named = members.find(m => (m.mediaEventName || '').trim())
    specs.push({
      displayName: (named?.mediaEventName || '').trim() || 'Inserted image seen',
      // The inserted markup carries data-tnx-media-group on each <img>.
      selector: `img[data-tnx-media-group="${groupId}"]`,
      tag: `group:${groupId}`,
    })
  }

  const usedSlugs = new Set<string>()
  for (const spec of specs) {
    try {
      // Disambiguate within this batch (same display name on multiple events).
      let slug = toEventNameSlug(spec.displayName)
      if (usedSlugs.has(slug)) {
        let n = 2
        while (usedSlugs.has(`${slug}_${n}`)) n++
        slug = `${slug}_${n}`
      }
      usedSlugs.add(slug)

      const eventName = `${slug}_${expToken}`.slice(0, 50).replace(/_+$/, '')

      const request: CreateCustomEventRequest = {
        store_id: storeId,
        event_name: eventName,
        display_name: spec.displayName,
        description: `Visibility of an image inserted by the visual editor (experiment ${experimentId}).`,
        category: 'engagement',
        tracking_config: {
          mode: 'auto',
          auto_config: {
            interaction_type: 'media_view',
            media_view_config: {
              match_by: 'selector',
              selector: spec.selector,
              // Visibility events fire on any intersection — no threshold UI.
              threshold: 0,
            },
          },
        },
        experiment_id: experimentId,
        variant_scope: 'treatment',
        tags: ['visual-editor', 'experiment', 'media-view', spec.tag],
      }

      await customEventsService.create(request)
      result.created++
    } catch (err) {
      result.failed++
      console.error('[TNX] Failed to create media_view event:', spec.tag, err)
    }
  }

  return result
}
